import React from 'react';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';
import { connect, useSelector } from 'react-redux';
import { DefaultTheme, Div, Icon, Text } from '@pinpoint/design-kit';
import get from 'lodash.get';

import { BreadcrumbsView, BreadcrumbsLink, Title } from 'src/components';
import { ModelProp } from 'src/store/typings';

export function CopyPersonalizationBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.blue}>
      <Div>
        <BreadcrumbsLink to="/copypx">
          <Title
            textSize="input"
            color="orange"
            icon="Text"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Copy Personalization"
          />
        </BreadcrumbsLink>
      </Div>
    </BreadcrumbsView>
  );
}

export function UsersBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.blue}>
      <Div>
        <BreadcrumbsLink to="/users">
          <Title
            textSize="input"
            color="blueLight"
            icon="Thinkalike"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Users"
          />
        </BreadcrumbsLink>
      </Div>
    </BreadcrumbsView>
  );
}

export function RtApiBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.marigold}>
      <Div>
        <BreadcrumbsLink to="/rtapi">
          <Title
            textSize="input"
            color="marigold"
            icon="Activate"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Real-Time API"
          />
        </BreadcrumbsLink>
      </Div>
    </BreadcrumbsView>
  );
}

export function RtApiNewBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.marigold}>
      <Div>
        <BreadcrumbsLink to="/rt">
          <Title
            textSize="input"
            color="marigold"
            icon="Activate"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Real-Time API"
          />
        </BreadcrumbsLink>
      </Div>
    </BreadcrumbsView>
  );
}

function RtApiDemoBreadcrumbView({ config }) {
  const modelName = get(config, 'config_metadata.model.name');
  return (
    <BreadcrumbsView color={DefaultTheme.colors.marigold}>
      <Div d="flex" align="baseline">
        <BreadcrumbsLink to="/rt">
          <Title
            textSize="input"
            color="marigold"
            icon="Activate"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Real-Time API"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="marigold" textWeight="200">
          Demo
        </Text>
      </Div>
      {modelName ? (
        <Div>
          <Text textSize="input" textColor="marigold" textWeight="200">
            {modelName}
          </Text>
        </Div>
      ) : null}
    </BreadcrumbsView>
  );
}

RtApiDemoBreadcrumbView.defaultProps = {
  config: {},
};

RtApiDemoBreadcrumbView.propTypes = {
  config: PropTypes.shape({
    config_metadata: PropTypes.shape({
      v: PropTypes.number.isRequired,
      model_id: PropTypes.number,
      model: ModelProp,
    }).isRequired,
  }),
};

const mapRtApiDemoStateToProps = ({ rtApiDemo: { config = {} } }, ownProps) => ({
  config,
  ...ownProps,
});

export const RtApiDemoBreadcrumb = connect(mapRtApiDemoStateToProps)(RtApiDemoBreadcrumbView);

export function RtApiDocumentationBreadcrumbView({ config }) {
  const modelName = get(config, 'config_metadata.model.name');
  return (
    <BreadcrumbsView color={DefaultTheme.colors.marigold}>
      <Div>
        <BreadcrumbsLink to="/rt">
          <Title
            textSize="input"
            color="marigold"
            icon="Activate"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Real-Time API"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="marigold" textWeight="200">
          Documentation
        </Text>
      </Div>
      {modelName ? (
        <Div>
          <Text textSize="input" textColor="marigold" textWeight="200">
            {modelName}
          </Text>
        </Div>
      ) : null}
    </BreadcrumbsView>
  );
}

const mapRtApiDocumentationStateToProps = ({ rtApiDemo: { config = {} } }, ownProps) => ({
  config,
  ...ownProps,
});

RtApiDocumentationBreadcrumbView.defaultProps = {
  config: {},
};

RtApiDocumentationBreadcrumbView.propTypes = {
  config: PropTypes.shape({
    config_metadata: PropTypes.shape({
      v: PropTypes.number.isRequired,
      model_id: PropTypes.number,
      model: ModelProp,
    }).isRequired,
  }),
};

export const RtApiDocumentationBreadcrumb = connect(mapRtApiDocumentationStateToProps)(
  RtApiDocumentationBreadcrumbView
);

export function RtApiUsageBreadcrumbView() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.marigold}>
      <Div>
        <BreadcrumbsLink to="/rt">
          <Title
            textSize="input"
            color="marigold"
            icon="Activate"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Real-Time API"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="marigold" textWeight="200">
          Usage
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

const mapRtApiUsageStateToProps = ({ rtApiDemo: { config = {} } }, ownProps) => ({
  config,
  ...ownProps,
});

export const RtApiUsageBreadcrumb = connect(mapRtApiUsageStateToProps)(RtApiUsageBreadcrumbView);

export function NewUsersBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.blue}>
      <Div>
        <BreadcrumbsLink to="/users">
          <Title
            textSize="input"
            color="blueLight"
            hoverColor="blueDark"
            icon="Thinkalike"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Users"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="blueLight" textWeight="200">
          New User
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

export function EditUsersBreadcrumbView({ userEmail }) {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.blue}>
      <Div>
        <BreadcrumbsLink to="/users">
          <Title
            textSize="input"
            color="blueLight"
            hoverColor="blue"
            icon="Thinkalike"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="2 0 50 50"
            text="Users"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="blueLight" textWeight="200">
          {userEmail || <Icon name="Loading4" viewBox="0 0 18 4" />}
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

EditUsersBreadcrumbView.propTypes = {
  userEmail: PropTypes.string.isRequired,
};

export const EditUsersBreadcrumb = connect(({ bc }) => bc)(EditUsersBreadcrumbView);

export function ClientSftpBreadcrumb() {
  return (
    <BreadcrumbsView>
      <Div>
        <BreadcrumbsLink to="/sftp">
          <Title
            textSize="input"
            color="blue"
            icon="Company"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="SFTP"
          />
        </BreadcrumbsLink>
      </Div>
    </BreadcrumbsView>
  );
}

export function ClientsBreadcrumb() {
  return (
    <BreadcrumbsView>
      <Div>
        <BreadcrumbsLink to="/clients">
          <Title
            textSize="input"
            color="blue"
            icon="Company"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Clients"
          />
        </BreadcrumbsLink>
      </Div>
    </BreadcrumbsView>
  );
}

export function AddClientBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.violet}>
      <Div>
        <BreadcrumbsLink to="/clients">
          <Title
            textSize="input"
            color="blue"
            icon="Company"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Clients"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="blue" textWeight="200">
          Add Client
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

export function EditClientBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.violet}>
      <Div>
        <BreadcrumbsLink to="/clients">
          <Title
            textSize="input"
            color="blue"
            icon="Company"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Clients"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="blue" textWeight="200">
          Edit Client
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

export function EditClientSubscribersBreadcrumb({ pathname }) {
  const crumbs = pathname.split('/').filter((x) => !!x);
  const [, clientId] = crumbs;
  return (
    <BreadcrumbsView color={DefaultTheme.colors.violet}>
      <Div>
        <BreadcrumbsLink to="/clients">
          <Title
            textSize="input"
            color="blue"
            icon="Company"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Clients"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="blue" textWeight="200">
          Edit Client
        </Text>
      </Div>
      <Div>
        <BreadcrumbsLink to={`/${crumbs.slice(0, 2).join('/')}`}>
          <Text textSize="input" textColor="blue" textWeight="200">
            {clientId}
          </Text>
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="blue" textWeight="200">
          Subscribers
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

EditClientSubscribersBreadcrumb.propTypes = {
  pathname: PropTypes.string.isRequired,
};

function OnboardingViewBreadcrumb({ datasetName }) {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.teal}>
      <Div d="flex" align="baseline">
        <BreadcrumbsLink to="/onboarding_landing">
          <Title
            textSize="input"
            color="teal"
            icon="Activate"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Onboarding"
          />
        </BreadcrumbsLink>
      </Div>
      <Div d="flex" align="baseline">
        <BreadcrumbsLink to="/onboarding">
          <Text textSize="input" textColor="teal" textWeight="200">
            Log
          </Text>
        </BreadcrumbsLink>
      </Div>
      <Div d="flex" align="baseline">
        <Text textSize="input" textColor="teal" textWeight="200">
          {datasetName}
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

OnboardingViewBreadcrumb.defaultProps = {};

OnboardingViewBreadcrumb.propTypes = {
  datasetName: PropTypes.string.isRequired,
};

const mapOnboardingViewStateToProps = ({ onboarding }) => ({ datasetName: onboarding?.dataset?.name });

export const OnboardingViewBreadcrumbView = connect(mapOnboardingViewStateToProps)(OnboardingViewBreadcrumb);

export function OnboardingLandingBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.teal}>
      <Div>
        <BreadcrumbsLink to="/onboarding_landing">
          <Title
            textSize="input"
            color="teal"
            icon="Onboard"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Onboarding"
          />
        </BreadcrumbsLink>
      </Div>
    </BreadcrumbsView>
  );
}

export function GenerateModelsBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.teal}>
      <Div>
        <BreadcrumbsLink to="/onboarding_landing">
          <Title
            textSize="input"
            color="teal"
            icon="Onboard"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Onboarding"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="teal" textWeight="200">
          Model
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

export function OnboardingBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.teal}>
      <Div>
        <BreadcrumbsLink to="/onboarding_landing">
          <Title
            textSize="input"
            color="teal"
            icon="Onboard"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Onboarding"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="teal" textWeight="200">
          Log
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

export function NewOnboardingBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.teal}>
      <Div>
        <BreadcrumbsLink to="/onboarding_landing">
          <Title
            textSize="input"
            color="teal"
            icon="Onboard"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Onboarding"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="teal" textWeight="200">
          New Onboarding
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

export function ReOnboardingBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.teal}>
      <Div>
        <BreadcrumbsLink to="/onboarding_landing">
          <Title
            textSize="input"
            color="teal"
            icon="Onboard"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 50 50"
            text="Onboarding"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="teal" textWeight="200">
          Re-Onboarding
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

export function DatasetsBreadcrumb() {
  return (
    <BreadcrumbsView>
      <BreadcrumbsLink to="/datasets">
        <Title
          textSize="input"
          color="green"
          icon="Crm"
          iconStyle={{ size: '1.375rem' }}
          iconViewBox="0 0 50 50"
          text="Datasets"
        />
      </BreadcrumbsLink>
    </BreadcrumbsView>
  );
}

export function SegmentsCompareBreadcrumbView({ segmentNames }) {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.green}>
      {segmentNames.length > 0 ? (
        <>
          <Div>
            <BreadcrumbsLink to="/datasets">
              <Title
                textSize="input"
                color="green"
                hoverColor="greenDark"
                icon="Crm"
                iconStyle={{ size: '1.375rem' }}
                iconViewBox="0 0 50 50"
                text="Datasets"
              />
            </BreadcrumbsLink>
          </Div>
          <Div>
            <Text textSize="input" textColor="green" textWeight="200">
              {segmentNames.join(' vs ')}
            </Text>
          </Div>
        </>
      ) : (
        ''
      )}
    </BreadcrumbsView>
  );
}

SegmentsCompareBreadcrumbView.propTypes = {
  segmentNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const mapSegmentsCompareStateToProps = ({ bc }) => bc;

export const SegmentsCompareBreadcrumb = connect(mapSegmentsCompareStateToProps)(SegmentsCompareBreadcrumbView);

function SegmentsViewBreadcrumbView({ datasetName, segmentName }) {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.green}>
      {segmentName || datasetName ? (
        <Div d="flex" align="baseline">
          <BreadcrumbsLink to="/datasets">
            <Title
              textSize="input"
              color="green"
              hoverColor="greenDark"
              icon="Crm"
              iconStyle={{ size: '1.375rem' }}
              iconViewBox="0 0 50 50"
              text="Datasets"
            />
          </BreadcrumbsLink>
          <Text textSize="input" textColor="green" textWeight="200">
            &ensp;/&ensp;
          </Text>
          <Text textSize="input" textColor="green" textWeight="200">
            {segmentName || datasetName}
          </Text>
        </Div>
      ) : null}
    </BreadcrumbsView>
  );
}

SegmentsViewBreadcrumbView.propTypes = {
  datasetName: PropTypes.string.isRequired,
  segmentName: PropTypes.string.isRequired,
};

const mapSegmentsViewStateToProps = ({ bc }) => bc;

export const SegmentsViewBreadcrumb = connect(mapSegmentsViewStateToProps)(SegmentsViewBreadcrumbView);

export function ModelsListBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.rioGrande}>
      <Div>
        <BreadcrumbsLink to="/models">
          <Title
            textSize="input"
            color="rioGrande"
            icon="ThinkalikeModel"
            iconViewBox="0 0 50 50"
            iconStyle={{ size: '1.375rem' }}
            text="Models"
          />
        </BreadcrumbsLink>
      </Div>
    </BreadcrumbsView>
  );
}

export function ModelAddBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.rioGrande}>
      <Div>
        <BreadcrumbsLink to="/models">
          <Title
            textSize="input"
            color="rioGrande"
            icon="ThinkalikeModel"
            iconViewBox="0 0 50 50"
            iconStyle={{ size: '1.375rem' }}
            text="Models"
          />
        </BreadcrumbsLink>
      </Div>
      <Div>
        <Text textSize="input" textColor="rioGrande" textWeight="200">
          Add
        </Text>
      </Div>
    </BreadcrumbsView>
  );
}

const modelFilter = ({ modelView }) => get(modelView, 'model', {});
const modelNameSelector = createSelector([modelFilter], (model) => model?.name);
export function ModelViewBreadcrumb() {
  const modelName = useSelector(modelNameSelector);
  return (
    <BreadcrumbsView color={DefaultTheme.colors.rioGrande}>
      {modelName ? (
        <>
          <Div>
            <BreadcrumbsLink to="/models">
              <Title
                textSize="input"
                color="rioGrande"
                hoverColor="rioGrandeDark"
                icon="ThinkalikeModel"
                iconViewBox="0 0 50 50"
                iconStyle={{ size: '1.375rem' }}
                text="Models"
              />
            </BreadcrumbsLink>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              Lift Charts
            </Text>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              {modelName}
            </Text>
          </Div>
        </>
      ) : (
        ''
      )}
    </BreadcrumbsView>
  );
}

function ModelComparisonBreadcrumbView({ modelNames }) {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.rioGrande}>
      {modelNames ? (
        <>
          <Div>
            <BreadcrumbsLink to="/models">
              <Title
                textSize="input"
                color="rioGrande"
                hoverColor="rioGrandeDark"
                icon="ThinkalikeModel"
                iconViewBox="0 0 50 50"
                iconStyle={{ size: '1.375rem' }}
                text="Models"
              />
            </BreadcrumbsLink>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              Model Comparison
            </Text>
          </Div>
        </>
      ) : (
        ''
      )}
    </BreadcrumbsView>
  );
}

ModelComparisonBreadcrumbView.propTypes = {
  modelNames: PropTypes.string.isRequired,
};

const mapModelComparisonToProps = ({ bc }) => bc;

export const ModelComparisonBreadcrumb = connect(mapModelComparisonToProps)(ModelComparisonBreadcrumbView);

function ModelImpactCalculatorComparisonBreadcrumbView({ modelNames }) {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.rioGrande}>
      {modelNames ? (
        <>
          <Div>
            <BreadcrumbsLink to="/models">
              <Title
                textSize="input"
                color="rioGrande"
                hoverColor="rioGrandeDark"
                icon="ThinkalikeModel"
                iconViewBox="0 0 50 50"
                iconStyle={{ size: '1.375rem' }}
                text="Models"
              />
            </BreadcrumbsLink>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              Impact Calculator Comparison
            </Text>
          </Div>
        </>
      ) : (
        ''
      )}
    </BreadcrumbsView>
  );
}

ModelImpactCalculatorComparisonBreadcrumbView.propTypes = {
  modelNames: PropTypes.string.isRequired,
};

const mapModelImpactCalculatorComparisonToProps = ({ bc }) => bc;

export const ModelImpactCalculatorComparisonBreadcrumb = connect(mapModelImpactCalculatorComparisonToProps)(
  ModelImpactCalculatorComparisonBreadcrumbView
);

function ModelInputDistributionsBreadcrumbView({ modelName }) {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.rioGrande}>
      {modelName ? (
        <>
          <Div>
            <BreadcrumbsLink to="/models">
              <Title
                textSize="input"
                color="rioGrande"
                hoverColor="rioGrandeDark"
                icon="ThinkalikeModel"
                iconViewBox="0 0 50 50"
                iconStyle={{ size: '1.375rem' }}
                text="Models"
              />
            </BreadcrumbsLink>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              Input Distributions
            </Text>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              {modelName}
            </Text>
          </Div>
        </>
      ) : (
        ''
      )}
    </BreadcrumbsView>
  );
}

ModelInputDistributionsBreadcrumbView.propTypes = {
  modelName: PropTypes.string.isRequired,
};

const mapModelInputDistributionsToProps = ({ bc }) => bc;

export const ModelInputDistributionsBreadcrumb = connect(mapModelInputDistributionsToProps)(
  ModelInputDistributionsBreadcrumbView
);

export function ModelValidationAnalyticsBreadcrumb() {
  const modelName = useSelector(modelNameSelector);
  return (
    <BreadcrumbsView color={DefaultTheme.colors.rioGrande}>
      {modelName ? (
        <>
          <Div>
            <BreadcrumbsLink to="/models">
              <Title
                textSize="input"
                color="rioGrande"
                hoverColor="rioGrandeDark"
                icon="ThinkalikeModel"
                iconViewBox="0 0 50 50"
                iconStyle={{ size: '1.375rem' }}
                text="Models"
              />
            </BreadcrumbsLink>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              Validation Analytics
            </Text>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              {modelName}
            </Text>
          </Div>
        </>
      ) : (
        ''
      )}
    </BreadcrumbsView>
  );
}

export function ModelRoiCalculatorBreadcrumb() {
  const modelName = useSelector(modelNameSelector);
  return (
    <BreadcrumbsView color={DefaultTheme.colors.rioGrande}>
      {modelName ? (
        <>
          <Div>
            <BreadcrumbsLink to="/models">
              <Title
                textSize="input"
                color="rioGrande"
                hoverColor="rioGrandeDark"
                icon="ThinkalikeModel"
                iconViewBox="0 0 50 50"
                iconStyle={{ size: '1.375rem' }}
                text="Models"
              />
            </BreadcrumbsLink>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              Impact Calculator
            </Text>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              {modelName}
            </Text>
          </Div>
        </>
      ) : (
        ''
      )}
    </BreadcrumbsView>
  );
}

export function ModelFairnessBreadcrumb() {
  const modelName = useSelector(modelNameSelector);
  return (
    <BreadcrumbsView color={DefaultTheme.colors.rioGrande}>
      {modelName ? (
        <>
          <Div>
            <BreadcrumbsLink to="/models">
              <Title
                textSize="input"
                color="rioGrande"
                hoverColor="rioGrandeDark"
                icon="ThinkalikeModel"
                iconViewBox="0 0 50 50"
                iconStyle={{ size: '1.375rem' }}
                text="Models"
              />
            </BreadcrumbsLink>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              Fairness
            </Text>
          </Div>
          <Div>
            <Text textSize="input" textColor="rioGrande" textWeight="200">
              {modelName}
            </Text>
          </Div>
        </>
      ) : (
        ''
      )}
    </BreadcrumbsView>
  );
}

export function RegulatoryBreadcrumb() {
  return (
    <BreadcrumbsView color={DefaultTheme.colors.orange}>
      <Div>
        <BreadcrumbsLink to="/regulatory">
          <Title
            textSize="input"
            color="orange"
            icon="Regulatory"
            iconStyle={{ size: '1.375rem' }}
            iconViewBox="0 0 38 38"
            text="Regulatory"
          />
        </BreadcrumbsLink>
      </Div>
    </BreadcrumbsView>
  );
}
